'use client'

import { useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/components/auth/auth-provider'
import { signInWithEmail, resetPasswordForEmail } from '@/lib/auth/client'
import { toast } from 'react-hot-toast'
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react'
import { Mail01 as Mail, Lock01 as Lock } from '@untitledui/icons'
import { Input } from '@/components/base/input/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'

export default function Home() {
  const { user, loading } = useAuth()
  const { branding } = useBranding()
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)

  // Redirect to dashboard if authenticated
  useEffect(() => {
    if (!loading && user) {
      router.replace('/dashboard')
    }
  }, [user, loading, router])

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setSigningIn(true)

    try {
      const { error } = await signInWithEmail(email, password)
      
      if (error) {
        throw error
      }

      toast.success('Successfully signed in!')
      
      // Small delay to ensure session is established
      setTimeout(() => {
        router.push('/dashboard')
      }, 100)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign in')
    } finally {
      setSigningIn(false)
    }
  }


  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetLoading(true)

    try {
      const { error } = await resetPasswordForEmail(resetEmail)

      if (error) {
        throw error
      }

      toast.success('Password reset email sent! Check your inbox.')
      setShowForgotPassword(false)
      setResetEmail('')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send reset email')
    } finally {
      setResetLoading(false)
    }
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="login-card text-center p-8 rounded-2xl shadow-2xl">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If user is authenticated, they'll be redirected via useEffect
  // Show sign-in form for unauthenticated users
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary">
      <Card className="login-card w-full max-w-md shadow-2xl">
        <CardContent className="pt-6">
          {/* Header */}
          <div className="text-center mb-8">
            <img
              src={branding.companyLogoUrl}
              alt={branding.companyName}
              className="w-64 mx-auto mb-4"
            />
            <p className="text-base-content/70 mt-2">Sign in to your account</p>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSignIn} className="space-y-6">
            <Input
              label="Email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(value) => setEmail(value)}
              icon={Mail}
              required
            />

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(value) => setPassword(value)}
                  icon={Lock}
                  required
                  inputClassName="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="modern"
              className="w-full"
              disabled={signingIn}
            >
              {signingIn && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {signingIn ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <div className="text-center mt-6 text-sm text-base-content/70">
            <p>Need access? Contact your system administrator.</p>
          </div>
        </CardContent>
      </Card>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal modal-open">
          <div className="modal-box login-card">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Reset Password
            </h3>
            <p className="py-4 text-base-content/70">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(value) => setResetEmail(value)}
                icon={Mail}
                required
              />
              
              <div className="modal-action">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetEmail('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="modern"
                  disabled={resetLoading}
                >
                  {resetLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {resetLoading ? 'Sending...' : 'Send Reset Email'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
